import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  alpha,
  keyframes,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getStaticFileUrl } from '../../utils/staticUtils';
import { AUTH_LOGO_STATIC_PATH } from './loginDarkTheme';
import {
  AUTH_BRANDING_ACCENT as ACCENT,
  AUTH_BRANDING_BORDER as BORDER,
  AUTH_BRANDING_DARK as DARK,
  AUTH_BRANDING_HERO_STATS as HERO_STATS,
  AUTH_BRANDING_SURFACE as SURFACE,
  AUTH_BRANDING_TEXT_BODY as TEXT_BODY,
  AUTH_BRANDING_TEXT_MUTED as TEXT_MUTED,
  AUTH_BRANDING_TEXT_PRIMARY as TEXT_PRIMARY,
  ROOMNEXA_APP_STORE_URL,
  ROOMNEXA_PLAY_STORE_URL,
} from './authBrandingTheme';
import AnimatedGridPattern, { BorderBeam } from './AnimatedGridPattern';
import './authPages.css';

const driftGlow = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); opacity: 0.75; }
  50% { transform: translate3d(-18px, 14px, 0) scale(1.08); opacity: 1; }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(1.03); }
`;

function AppStoreBadge({ store }) {
  const isApple = store === 'apple';

  return (
    <Paper
      component="a"
      href={isApple ? ROOMNEXA_APP_STORE_URL : ROOMNEXA_PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={isApple ? 'Download RoomNexa on the App Store' : 'Get RoomNexa on Google Play'}
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.25,
        py: 1.35,
        minWidth: 158,
        flexShrink: 0,
        borderRadius: 3,
        textDecoration: 'none',
        color: TEXT_PRIMARY,
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        transition: 'transform 0.25s ease, border-color 0.25s ease, background-color 0.25s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: alpha(ACCENT, 0.35),
          bgcolor: alpha(ACCENT, 0.06),
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(ACCENT, 0.1),
          border: `1px solid ${alpha(ACCENT, 0.18)}`,
        }}
      >
        {isApple ? (
          <Box component="svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </Box>
        ) : (
          <Box component="svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 20.5V3.5C3 2.91 3.34 2.39 3.84 2.15L13.69 12L3.84 21.85C3.34 21.61 3 21.09 3 20.5Z" />
            <path d="M16.81 15.12L6.05 21.34L14.54 12.85L16.81 15.12Z" />
            <path d="M14.54 11.15L6.05 2.66L16.81 8.88L14.54 11.15Z" />
            <path d="M20.16 4.83C19.69 4.58 19.11 4.58 18.64 4.83L11.64 9.15L14.54 12.05L21.55 7.72C22.02 7.47 22.02 6.92 21.55 6.67L20.16 4.83Z" />
            <path d="M21.55 16.28L14.54 11.95L11.64 14.85L18.64 19.17C19.11 19.42 19.69 19.42 20.16 19.17L21.55 17.33C22.02 17.08 22.02 16.53 21.55 16.28Z" />
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.62rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            color: TEXT_MUTED,
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
          }}
        >
          {isApple ? 'Download on the' : 'GET IT ON'}
        </Typography>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.98rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            color: TEXT_PRIMARY,
            whiteSpace: 'nowrap',
          }}
        >
          {isApple ? 'App Store' : 'Google Play'}
        </Typography>
      </Box>
    </Paper>
  );
}

function StatsBar({ stats = HERO_STATS, columns = 2 }) {
  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        width: '100%',
        borderRadius: 3,
        border: `1px solid ${BORDER}`,
        bgcolor: SURFACE,
        backdropFilter: 'blur(12px)',
        p: { md: 1.75, lg: 2 },
        boxShadow: `0 24px 64px rgba(0,0,0,0.55), inset 0 0 0 1px ${alpha(ACCENT, 0.08)}`,
        overflow: 'hidden',
      }}
    >
      <BorderBeam colorFrom={ACCENT} colorTo="#22d3ee" duration={10} size={140} />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: { md: `repeat(${columns}, 1fr)` },
          gap: 1.25,
        }}
      >
        {stats.map((stat) => (
          <Box
            key={stat.label}
            sx={{
              borderRadius: 2,
              border: `1px solid ${BORDER}`,
              bgcolor: 'rgba(255,255,255,0.02)',
              px: 1.75,
              py: 1.5,
              textAlign: 'left',
            }}
          >
            <Typography component="span" sx={{ color: ACCENT, fontWeight: 800, fontSize: '1.25rem', lineHeight: 1.1, letterSpacing: '-0.03em', mb: 0.4 }}>
              {stat.value}
            </Typography>
            <Typography component="span" sx={{ color: TEXT_MUTED, fontSize: '0.76rem', lineHeight: 1.45, fontWeight: 500, display: 'block' }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function DefaultContent() {
  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { md: 3, lg: 3.5 },
          width: '100%',
          borderRadius: 3,
          bgcolor: SURFACE,
          border: `1px solid ${BORDER}`,
          backdropFilter: 'blur(12px)',
          boxShadow: `0 24px 64px rgba(0,0,0,0.5), inset 0 0 0 1px ${alpha(ACCENT, 0.06)}`,
        }}
      >
        <Typography
          component="div"
          role="heading"
          aria-level={2}
          sx={{
            fontSize: { md: '1.35rem', lg: '1.5rem' },
            fontWeight: 800,
            lineHeight: 1.35,
            letterSpacing: '-0.03em',
            mb: 1.25,
            color: TEXT_PRIMARY,
            textAlign: 'center',
            textShadow: '0 1px 12px rgba(0,0,0,0.35)',
          }}
        >
          <Box component="span" sx={{ display: 'block', color: TEXT_PRIMARY }}>
            You focus on guests.
          </Box>
          <Box component="span" sx={{ display: 'block', color: TEXT_PRIMARY }}>
            <Box component="span" sx={{ color: ACCENT }}>
              AI
            </Box>{' '}
            takes care of the rest.
          </Box>
        </Typography>

        <Typography component="span" sx={{ color: TEXT_BODY, fontSize: '0.92rem', lineHeight: 1.7, mb: 2.5, textAlign: 'center', fontWeight: 500, display: 'block' }}>
          A next-generation cloud and AI platform for hotels and vacation rentals.
        </Typography>

        <Typography component="span" sx={{ color: TEXT_MUTED, fontSize: '0.82rem', fontWeight: 600, textAlign: 'center', display: 'block' }}>
          Trusted by 1,000+ hotels globally to run smarter
        </Typography>
      </Paper>

      <StatsBar />

      <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
        <AppStoreBadge store="apple" />
        <AppStoreBadge store="google" />
      </Stack>
    </>
  );
}

function FreeTrialContent() {
  const steps = [
    'Your free-trial account is created instantly after successful signup.',
    'Login credentials are sent to your hotel official email.',
    'RoomNexa team can help with setup, onboarding, and first hotel configuration.',
    'No credit card required to start.',
  ];

  return (
    <>
      <Typography
        component="div"
        role="heading"
        aria-level={2}
        sx={{
          fontSize: { md: '1.45rem', lg: '1.58rem' },
          fontWeight: 900,
          letterSpacing: '-0.03em',
          lineHeight: 1.25,
          mb: 1.25,
          color: TEXT_PRIMARY,
          textAlign: 'center',
          textShadow: '0 1px 12px rgba(0,0,0,0.35)',
        }}
      >
        Start Your{' '}
        <Box component="span" sx={{ color: ACCENT }}>
          Free 30-Day Trial
        </Box>
      </Typography>

      <Typography component="span" sx={{ color: TEXT_BODY, fontSize: '0.95rem', lineHeight: 1.75, mb: 2.5, maxWidth: 420, mx: 'auto', fontWeight: 500, display: 'block' }}>
        Create your RoomNexa trial account instantly from the same secure console used by the web and mobile apps.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          width: '100%',
          borderRadius: 3,
          textAlign: 'left',
          bgcolor: SURFACE,
          border: `1px solid ${BORDER}`,
          backdropFilter: 'blur(12px)',
          boxShadow: `0 20px 48px rgba(0,0,0,0.45), inset 0 0 0 1px ${alpha(ACCENT, 0.06)}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <AutoAwesomeIcon sx={{ color: ACCENT, fontSize: 18 }} />
          <Typography component="span" sx={{ fontWeight: 800, color: TEXT_PRIMARY, fontSize: '0.95rem' }}>What happens next?</Typography>
        </Stack>
        <Box component="ul" sx={{ m: 0, pl: 2.2, color: TEXT_MUTED, lineHeight: 1.8, fontSize: '0.88rem', fontWeight: 500 }}>
          {steps.map((step) => (
            <Box component="li" key={step} sx={{ mb: 0.35 }}>
              {step}
            </Box>
          ))}
        </Box>
      </Paper>

      <StatsBar
        stats={[
          { value: '30 Days', label: 'Free trial included' },
          { value: 'Instant', label: 'Account creation after signup' },
        ]}
        columns={2}
      />
    </>
  );
}

export default function AuthBrandingPanel({ variant = 'default' }) {
  return (
    <Box
      className="auth-branding-panel"
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '50%',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        color: TEXT_PRIMARY,
        p: { md: 4, lg: 6 },
        bgcolor: DARK,
      }}
    >
      {/* Animated grid — matches roomnexa.com hero */}
      <AnimatedGridPattern
        numSquares={36}
        maxOpacity={0.22}
        duration={2.2}
        repeatDelay={0.18}
        stagger={0.045}
        strokeColor="rgba(76,225,182,0.1)"
        fillColor="rgba(76,225,182,0.45)"
      />

      {/* Center glow */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: '50%',
          top: '38%',
          width: 520,
          height: 420,
          transform: 'translate3d(-50%, -50%, 0)',
          background: `radial-gradient(ellipse at center, ${alpha(ACCENT, 0.14)} 0%, ${alpha(ACCENT, 0.05)} 42%, transparent 72%)`,
          filter: 'blur(48px)',
          pointerEvents: 'none',
          animation: `${driftGlow} 14s ease-in-out infinite`,
        }}
      />

      {/* Bottom-right soft blob */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: '-8%',
          bottom: '-12%',
          width: 360,
          height: 360,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(ACCENT, 0.12)} 0%, transparent 68%)`,
          filter: 'blur(40px)',
          pointerEvents: 'none',
          animation: `${driftGlow} 18s ease-in-out infinite reverse`,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 480,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 2.75,
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <Box
            sx={{
              position: 'absolute',
              inset: -14,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(ACCENT, 0.22)} 0%, transparent 70%)`,
              filter: 'blur(16px)',
              animation: `${pulseGlow} 5s ease-in-out infinite`,
            }}
          />
          <Paper
            elevation={0}
            sx={{
              width: 104,
              height: 104,
              borderRadius: '24px',
              display: 'grid',
              placeItems: 'center',
              position: 'relative',
              bgcolor: SURFACE,
              border: `1px solid ${BORDER}`,
              boxShadow: `0 16px 40px rgba(0,0,0,0.5), inset 0 0 0 1px ${alpha(ACCENT, 0.08)}`,
            }}
          >
            <Box
              component="img"
              src={getStaticFileUrl(AUTH_LOGO_STATIC_PATH)}
              alt="RoomNexa"
              sx={{ width: 68, height: 68, objectFit: 'contain', filter: `drop-shadow(0 2px 8px ${alpha(ACCENT, 0.25)})` }}
            />
          </Paper>
        </Box>

        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 0.65,
            borderRadius: 999,
            bgcolor: alpha(ACCENT, 0.14),
            border: `1px solid ${alpha(ACCENT, 0.42)}`,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 14, color: '#ffffff' }} />
          <Typography
            component="span"
            className="auth-branding-badge-text"
            sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#ffffff', letterSpacing: '0.06em' }}
          >
            AI hospitality platform
          </Typography>
        </Box>

        {variant === 'free-trial' ? <FreeTrialContent /> : <DefaultContent />}
      </Box>
    </Box>
  );
}
